import { useEffect, useMemo, useState } from "react";
import { useAuth } from "context/AuthContext";
import { isAdminUser } from "modules/systems/services/application.service";
import { checkPermissionsBatch } from "services/permission.service";
import { PERMISSIONS } from "auth/permissionCodes";

export const SUBCONTRACT_PERMISSIONS = {
  dashboardRead: PERMISSIONS.SUBCONTRACT.DASHBOARD_READ,
  subcontractRead: PERMISSIONS.SUBCONTRACT.SUBCONTRACT_READ,
  subcontractCreate: PERMISSIONS.SUBCONTRACT.SUBCONTRACT_CREATE,
  subcontractUpdate: PERMISSIONS.SUBCONTRACT.SUBCONTRACT_UPDATE,
  subcontractDelete: PERMISSIONS.SUBCONTRACT.SUBCONTRACT_DELETE,
  acceptanceRead: PERMISSIONS.SUBCONTRACT.SUBCONTRACT_ACCEPTANCE_READ,
  acceptanceCreate: PERMISSIONS.SUBCONTRACT.SUBCONTRACT_ACCEPTANCE_CREATE,
  acceptanceUpdate: PERMISSIONS.SUBCONTRACT.SUBCONTRACT_ACCEPTANCE_UPDATE,
  acceptanceDelete: PERMISSIONS.SUBCONTRACT.SUBCONTRACT_ACCEPTANCE_DELETE,
  acceptanceReportedQuantityUpdate: PERMISSIONS.SUBCONTRACT.DRAFT_ACCEPTANCE_ITEM_UPDATE,
  acceptanceApprovalRead: PERMISSIONS.SUBCONTRACT.ACCEPTANCE_APPROVAL_READ,
  acceptanceApprovalSubmit: PERMISSIONS.SUBCONTRACT.ACCEPTANCE_APPROVAL_SUBMIT,
  acceptanceApprovalApprove: PERMISSIONS.SUBCONTRACT.ACCEPTANCE_APPROVAL_APPROVE,
  approvalRead: PERMISSIONS.SUBCONTRACT.SUBCONTRACT_APPROVAL_READ,
  approvalCreate: PERMISSIONS.SUBCONTRACT.SUBCONTRACT_APPROVAL_SUBMIT,
  approvalUpdate: PERMISSIONS.SUBCONTRACT.SUBCONTRACT_APPROVAL_SUBMIT,
  approvalDelete: PERMISSIONS.SUBCONTRACT.SUBCONTRACT_APPROVAL_SUBMIT,
  approvalSubmit: PERMISSIONS.SUBCONTRACT.SUBCONTRACT_APPROVAL_SUBMIT,
  approvalApprove: PERMISSIONS.SUBCONTRACT.SUBCONTRACT_APPROVAL_APPROVE,
  paymentRead: PERMISSIONS.SUBCONTRACT.SUBCONTRACT_PAYMENT_READ,
  paymentCreate: PERMISSIONS.SUBCONTRACT.SUBCONTRACT_PAYMENT_CREATE,
  paymentUpdate: PERMISSIONS.SUBCONTRACT.SUBCONTRACT_PAYMENT_UPDATE,
  paymentDelete: PERMISSIONS.SUBCONTRACT.SUBCONTRACT_PAYMENT_DELETE,
};

const allPermissionNames = Object.values(SUBCONTRACT_PERMISSIONS);

export default function useSubcontractPermissions() {
  const { user } = useAuth();
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!user?.userId) {
        setResults({});
        setLoading(false);
        return;
      }

      if (isAdminUser(user)) {
        setResults(Object.fromEntries(allPermissionNames.map((permission) => [permission, true])));
        setLoading(false);
        return;
      }

      setLoading(true);
      const response = await checkPermissionsBatch(allPermissionNames);
      if (active) {
        setResults(response.results || {});
        setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [user]);

  return useMemo(() => {
    const has = (permission) => Boolean(results[permission]);
    return {
      loading,
      has,
      canReadAcceptance:
        has(SUBCONTRACT_PERMISSIONS.acceptanceRead) ||
        has(SUBCONTRACT_PERMISSIONS.acceptanceReportedQuantityUpdate),
      canUpdateAcceptance:
        has(SUBCONTRACT_PERMISSIONS.acceptanceUpdate) ||
        has(SUBCONTRACT_PERMISSIONS.acceptanceReportedQuantityUpdate),
    };
  }, [loading, results]);
}
